import { useEffect, useState, useRef, useMemo } from "react";
import { IMAGE_SHARING_ENABLED } from "@/lib/featureFlags";
import { Link } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { ClubCrest } from "@/components/ClubCrest";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { useQuery } from "@tanstack/react-query";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { CalendarIcon, Table, Share2, Home, Plane, ChevronDown } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { getSeasonsInTenure, getSeasonYearForDate, getSeasonDateRange, type SeasonType } from "@/lib/seasonUtils";

type VenueFilter = "all" | "home" | "away";
import { cn } from "@/lib/utils";
import { ShareableTableCustomizer } from "@/components/ShareableTableCustomizer";
import { useClubSlugMap } from "@/hooks/useClubDirectory";
import { API_VERSION } from "@/lib/apiVersion";
import { useAuth } from "@/contexts/AuthContext";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
const getPositionColor = (position: number, totalTeams: number, isManagerTeam: boolean, config?: LeagueQualificationConfig | null) => {
  if (!config) {
    // Fallback to basic logic
    if (isManagerTeam) {
      if (position <= 4) return { textColor: "text-green-700 dark:text-green-300", bgColor: "bg-green-500/15", posColor: "text-green-700 dark:text-green-300" };
      if (position > totalTeams - 3) return { textColor: "text-red-700 dark:text-red-400", bgColor: "bg-red-600/25", posColor: "text-red-700 dark:text-red-400" };
      return { textColor: "", bgColor: "bg-blue-100/50 dark:bg-blue-950/30", posColor: "" };
    }
    if (position <= 4) return { textColor: "", bgColor: "bg-primary/5", posColor: "text-primary" };
    if (position > totalTeams - 3) return { textColor: "", bgColor: "bg-destructive/5", posColor: "text-destructive" };
    return { textColor: "", bgColor: "", posColor: "" };
  }

  const { ucl_spots, uel_spots, uecl_spots, relegation_zone, team_count, promotion_spots, playoff_spots } = config;
  const actualTeamCount = team_count || totalTeams;
  const isSecondTier = promotion_spots > 0 && ucl_spots === 0 && uel_spots === 0 && uecl_spots === 0;

  // Determine zone
  let zone: "ucl" | "uel" | "uecl" | "promotion" | "playoff" | "relegation" | "mid" = "mid";

  if (isSecondTier) {
    if (position <= promotion_spots) zone = "promotion";
    else if (playoff_spots > 0 && position <= promotion_spots + playoff_spots) zone = "playoff";
    else if (relegation_zone > 0 && position > actualTeamCount - relegation_zone) zone = "relegation";
  } else {
    if (ucl_spots > 0 && position <= ucl_spots) zone = "ucl";
    else if (uel_spots > 0 && position <= ucl_spots + uel_spots) zone = "uel";
    else if (uecl_spots > 0 && position <= ucl_spots + uel_spots + uecl_spots) zone = "uecl";
    else if (relegation_zone > 0 && position > actualTeamCount - relegation_zone) zone = "relegation";
  }

  // Color map for manager team (stronger) vs other teams
  const zoneColors: Record<string, { manager: { textColor: string; bgColor: string; posColor: string }; other: { textColor: string; bgColor: string; posColor: string } }> = {
    ucl: {
      manager: { textColor: "text-blue-700 dark:text-blue-300", bgColor: "bg-blue-600/25", posColor: "text-blue-700 dark:text-blue-300" },
      other: { textColor: "", bgColor: "bg-blue-600/10", posColor: "text-blue-600 dark:text-blue-400" },
    },
    uel: {
      manager: { textColor: "text-orange-700 dark:text-orange-300", bgColor: "bg-orange-500/25", posColor: "text-orange-700 dark:text-orange-300" },
      other: { textColor: "", bgColor: "bg-orange-500/10", posColor: "text-orange-700 dark:text-orange-400" },
    },
    uecl: {
      manager: { textColor: "text-green-700 dark:text-green-300", bgColor: "bg-green-600/20", posColor: "text-green-700 dark:text-green-300" },
      other: { textColor: "", bgColor: "bg-green-600/10", posColor: "text-green-700 dark:text-green-400" },
    },
    promotion: {
      manager: { textColor: "text-emerald-700 dark:text-emerald-300", bgColor: "bg-emerald-500/25", posColor: "text-emerald-700 dark:text-emerald-300" },
      other: { textColor: "", bgColor: "bg-emerald-500/15", posColor: "text-emerald-600" },
    },
    playoff: {
      manager: { textColor: "text-sky-700 dark:text-sky-300", bgColor: "bg-sky-500/25", posColor: "text-sky-700 dark:text-sky-300" },
      other: { textColor: "", bgColor: "bg-sky-500/10", posColor: "text-sky-600" },
    },
    relegation: {
      manager: { textColor: "text-red-700 dark:text-red-400", bgColor: "bg-red-600/25", posColor: "text-red-700 dark:text-red-400" },
      other: { textColor: "", bgColor: "bg-destructive/10", posColor: "text-destructive" },
    },
    mid: {
      manager: { textColor: "", bgColor: "bg-blue-100/50 dark:bg-blue-950/30", posColor: "" },
      other: { textColor: "", bgColor: "", posColor: "" },
    },
  };

  return isManagerTeam ? zoneColors[zone].manager : zoneColors[zone].other;
};

interface ManagerTableSectionProps {
  managerId: string;
  onSelectManager?: (managerId: string) => void;
  onAssignmentChange?: (assignmentId: string, assignment: Assignment | null) => void;
  onDateRangeChange?: (fromDate: Date | undefined, toDate: Date | undefined) => void;
}

interface TableRow {
  position: number;
  club_id: string;
  club_name: string;
  played: number;
  won: number;
  drawn: number;
  lost: number;
  gf: number;
  ga: number;
  gd: number;
  pts: number;
  home_played: number;
  home_won: number;
  home_drawn: number;
  home_lost: number;
  home_gf: number;
  home_ga: number;
  home_pts: number;
  away_played: number;
  away_won: number;
  away_drawn: number;
  away_lost: number;
  away_gf: number;
  away_ga: number;
  away_pts: number;
  managers?: string[];
}

interface ManagerInfo {
  name: string;
  club_name: string;
  start_date: string;
  end_date: string | null;
  league: string;
  leagueCode?: string;
}

interface LeagueQualificationConfig {
  ucl_spots: number;
  uel_spots: number;
  uecl_spots: number;
  relegation_zone: number;
  team_count: number;
  promotion_spots: number;
  playoff_spots: number;
}

export interface Assignment {
  id: string;
  start_date: string;
  end_date: string | null;
  league: string;
  club_id: string;
  clubs: { name: string };
  managers: { name: string };
  leagues: { display_name: string };
}

// Local getSeasonsInTenure / getSeasonYearForDate removed — now imported from seasonUtils

export const ManagerTableSection = ({ 
  managerId, 
  onSelectManager,
  onAssignmentChange,
  onDateRangeChange,
}: ManagerTableSectionProps) => {
  const [selectedAssignmentId, setSelectedAssignmentId] = useState<string>("");
  const [selectedSeason, setSelectedSeason] = useState<string>("");
  const [fromDate, setFromDate] = useState<Date | undefined>(undefined);
  const [toDate, setToDate] = useState<Date | undefined>(undefined);
  const [showAllTeams, setShowAllTeams] = useState<boolean>(false);
  const [showManagers, setShowManagers] = useState<boolean>(false);
  const [showShareCustomizer, setShowShareCustomizer] = useState<boolean>(false);
  const [venueFilter, setVenueFilter] = useState<VenueFilter>("all");
  const { user } = useAuth();
  const { getClubSlug } = useClubSlugMap();


  const { data: assignmentsData, isLoading: assignmentsLoading } = useQuery({
    queryKey: ["manager-assignments", managerId, API_VERSION],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("compute-manager-table", {
        body: { managerId },
      });
      if (error) throw error;
      return data;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  useEffect(() => {
    setSelectedAssignmentId("");
    setFromDate(undefined);
    setToDate(undefined);
  }, [managerId]);

  const assignments: Assignment[] = assignmentsData?.assignments || [];

  useEffect(() => {
    if (assignmentsData?.selectedAssignmentId && selectedAssignmentId === "") {
      setSelectedAssignmentId(assignmentsData.selectedAssignmentId);
    }
  }, [assignmentsData, selectedAssignmentId]);

  useEffect(() => {
    if (selectedAssignmentId && assignments.length > 0) {
      const assignment = assignments.find((a) => a.id === selectedAssignmentId);
      if (assignment) {
        const newFromDate = new Date(assignment.start_date);
        const newToDate = assignment.end_date ? new Date(assignment.end_date) : new Date();
        setFromDate(newFromDate);
        setToDate(newToDate);
        setSelectedSeason("");
        onAssignmentChange?.(selectedAssignmentId, assignment);
        onDateRangeChange?.(newFromDate, newToDate);
      }
    }
  }, [selectedAssignmentId, assignments]);

  useEffect(() => {
    onDateRangeChange?.(fromDate, toDate);
  }, [fromDate, toDate]);

  const handleSeasonSelect = (season: string) => {
    setSelectedSeason(season);
    const today = new Date();
    
    if (season === "current") {
      const seasonStartYear = getSeasonYearForDate(today, leagueSeasonType);
      if (leagueSeasonType === 'calendar-year') {
        setFromDate(new Date(seasonStartYear, 0, 1));
      } else {
        setFromDate(new Date(seasonStartYear, 7, 1));
      }
      setToDate(today);
    } else {
      const { start: seasonStart, end: seasonEnd } = getSeasonDateRange(season, leagueSeasonType);
      
      if (selectedAssignment) {
        const tenureStart = new Date(selectedAssignment.start_date);
        const tenureEnd = selectedAssignment.end_date ? new Date(selectedAssignment.end_date) : today;
        const seasonsData = getSeasonsInTenure(selectedAssignment.start_date, selectedAssignment.end_date, leagueSeasonType);
        const seasonData = seasonsData.find(s => s.season === season);
        
        if (seasonData?.isPartialFirst && tenureStart > seasonStart) {
          setFromDate(tenureStart);
        } else {
          setFromDate(seasonStart);
        }
        
        let effectiveEnd = seasonEnd;
        
        if (!selectedAssignment.end_date && seasonEnd > today) {
          effectiveEnd = today;
        } else if (seasonData?.isPartialLast && tenureEnd < seasonEnd) {
          effectiveEnd = tenureEnd;
        }
        
        setToDate(effectiveEnd);
      } else {
        setFromDate(seasonStart);
        setToDate(seasonEnd > today ? today : seasonEnd);
      }
    }
  };

  const selectedAssignment = assignments.find((a) => a.id === selectedAssignmentId);

  // Fetch season_type early — needed for season selector before full league config is available
  const earlyLeagueCode = selectedAssignment?.league;
  const { data: earlyLeagueData } = useQuery({
    queryKey: ["league-season-type", earlyLeagueCode],
    queryFn: async () => {
      if (!earlyLeagueCode) return null;
      const { data, error } = await supabase
        .from("leagues")
        .select("season_type")
        .eq("code", earlyLeagueCode)
        .single();
      if (error) return null;
      return data;
    },
    enabled: !!earlyLeagueCode,
    staleTime: 30 * 60 * 1000,
  });
  const leagueSeasonType: SeasonType = (earlyLeagueData?.season_type as SeasonType) || 'split-year';

  const availableSeasonsData = selectedAssignment
    ? getSeasonsInTenure(selectedAssignment.start_date, selectedAssignment.end_date, leagueSeasonType)
    : [];
  const availableSeasons = availableSeasonsData.map(s => s.season);
  
  const isActiveManager = selectedAssignment && !selectedAssignment.end_date;
  const seasonsWithCurrent = isActiveManager 
    ? ["current", ...availableSeasons] 
    : availableSeasons;

  // Guard: ensure selectedAssignmentId belongs to current manager's assignments
  const isAssignmentValid = selectedAssignmentId && assignments.some(a => a.id === selectedAssignmentId);

  const { data: tableData, isLoading: tableLoading } = useQuery({
    queryKey: [
      "manager-table",
      managerId,
      selectedAssignmentId,
      fromDate ? format(fromDate, "yyyy-MM-dd") : null,
      toDate ? format(toDate, "yyyy-MM-dd") : null,
      API_VERSION,
    ],
    queryFn: async () => {
      // Double-check assignment belongs to this manager before calling backend
      if (!selectedAssignmentId || !isAssignmentValid) {
        console.warn("[ManagerTableSection] Blocked request: assignmentId not in current manager's assignments", {
          managerId,
          selectedAssignmentId,
          availableAssignments: assignments.map(a => a.id),
        });
        return null;
      }

      const body: any = { managerId, assignmentId: selectedAssignmentId };

      if (fromDate && toDate) {
        body.customStartDate = format(fromDate, "yyyy-MM-dd");
        body.customEndDate = format(toDate, "yyyy-MM-dd");
      }

      const { data, error } = await supabase.functions.invoke("compute-manager-table", {
        body,
      });

      if (error) throw error;
      return data;
    },
    enabled: !!selectedAssignmentId && isAssignmentValid,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const managerInfo: ManagerInfo | null = tableData?.managerInfo || assignmentsData?.managerInfo;
  const leagueCode = managerInfo?.leagueCode || selectedAssignment?.league;
  const table: TableRow[] = tableData?.table || [];

  // Fetch league qualification config for dynamic zone colors
  const { data: leagueConfig } = useQuery({
    queryKey: ["league-config", leagueCode],
    queryFn: async () => {
      if (!leagueCode) return null;
      const { data, error } = await supabase
        .from("leagues")
        .select("ucl_spots, uel_spots, uecl_spots, relegation_zone, team_count, promotion_spots, playoff_spots, season_type")
        .eq("code", leagueCode)
        .single();
      if (error) return null;
      return data as LeagueQualificationConfig & { season_type: string };
    },
    enabled: !!leagueCode,
    staleTime: 30 * 60 * 1000,
  });

  

  // Apply venue filter: remap stats and re-sort/re-number positions
  const filteredTable = useMemo(() => {
    if (venueFilter === "all") return table;
    return table
      .map((row) => {
        const prefix = venueFilter === "home" ? "home_" : "away_";
        return {
          ...row,
          played: row[`${prefix}played` as keyof TableRow] as number ?? 0,
          won: row[`${prefix}won` as keyof TableRow] as number ?? 0,
          drawn: row[`${prefix}drawn` as keyof TableRow] as number ?? 0,
          lost: row[`${prefix}lost` as keyof TableRow] as number ?? 0,
          gf: row[`${prefix}gf` as keyof TableRow] as number ?? 0,
          ga: row[`${prefix}ga` as keyof TableRow] as number ?? 0,
          gd: ((row[`${prefix}gf` as keyof TableRow] as number) ?? 0) - ((row[`${prefix}ga` as keyof TableRow] as number) ?? 0),
          pts: row[`${prefix}pts` as keyof TableRow] as number ?? 0,
        };
      })
      .sort((a, b) => b.pts - a.pts || b.gd - a.gd || b.gf - a.gf || a.club_name.localeCompare(b.club_name))
      .map((row, i) => ({ ...row, position: i + 1 }));
  }, [table, venueFilter]);

  const defaultTeamCount = leagueConfig?.team_count || 20;
  const displayedTable = showAllTeams ? filteredTable : filteredTable.slice(0, defaultTeamCount);
  const isLoading = assignmentsLoading || (tableLoading && !managerInfo);

  const minDate = selectedAssignment ? new Date(selectedAssignment.start_date) : undefined;
  const maxDate = selectedAssignment && selectedAssignment.end_date 
    ? new Date(selectedAssignment.end_date) 
    : new Date();

  if (!managerInfo && isLoading) {
    return (
      <Card className="p-6">
        <div className="space-y-4">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-4 w-96" />
          <Skeleton className="h-64 w-full" />
        </div>
      </Card>
    );
  }

  if (!managerInfo) {
    return (
      <Card className="p-6">
        <p className="text-muted-foreground">No data found for this manager</p>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2">
            <Table className="h-5 w-5" />
            Tenure table
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            League table counting only the matches played during this tenure.
          </p>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Selectors Row */}
          <div className="flex flex-wrap gap-3">
            {assignments.length > 1 && (
              <div className="flex-1 min-w-[200px]">
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Assignment</label>
                <Select value={selectedAssignmentId} onValueChange={setSelectedAssignmentId}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select an assignment" />
                  </SelectTrigger>
                  <SelectContent>
                    {assignments.map((assignment) => (
                      <SelectItem key={assignment.id} value={assignment.id}>
                        {assignment.clubs.name} · {format(new Date(assignment.start_date), "MMM yy")} –{" "}
                        {assignment.end_date ? format(new Date(assignment.end_date), "MMM yy") : "now"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {seasonsWithCurrent.length > 0 && (
              <div className={cn("min-w-[150px]", assignments.length <= 1 ? "flex-1 max-w-xs" : "w-[180px]")}>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Season</label>
                <Select value={selectedSeason} onValueChange={handleSeasonSelect}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a season" />
                  </SelectTrigger>
                  <SelectContent>
                    {seasonsWithCurrent.map((season) => (
                      <SelectItem key={season} value={season}>
                        {season === "current" ? "Current season" : season}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Quick Stats */}
          {(() => {
            const managerRow = filteredTable.find((row) => row.club_name === managerInfo.club_name);
            if (!managerRow || managerRow.played === 0) return null;
            const ppg = (managerRow.pts / managerRow.played).toFixed(2);
            const winPct = ((managerRow.won / managerRow.played) * 100).toFixed(0);
            const ppgLabel = venueFilter === "home" ? "Home PPG" : venueFilter === "away" ? "Away PPG" : "PPG";
            return (
              <div className="flex flex-wrap gap-3 border-t pt-3">
                <div className="rounded-md border bg-muted/30 px-3 py-1.5 text-sm">
                  <span className="text-muted-foreground">{ppgLabel}</span>{" "}
                  <span className="font-semibold text-foreground">{ppg}</span>
                </div>
                <div className="rounded-md border bg-muted/30 px-3 py-1.5 text-sm">
                  <span className="text-muted-foreground">Win%</span>{" "}
                  <span className="font-semibold text-foreground">{winPct}%</span>
                </div>
                <div className="rounded-md border bg-muted/30 px-3 py-1.5 text-sm">
                  <span className="text-muted-foreground">Record</span>{" "}
                  <span className="font-semibold text-foreground">{managerRow.won}-{managerRow.drawn}-{managerRow.lost}</span>
                </div>
                <div className="rounded-md border bg-muted/30 px-3 py-1.5 text-sm">
                  <span className="text-muted-foreground">GD</span>{" "}
                  <span className={cn("font-semibold", managerRow.gd > 0 ? "text-green-700 dark:text-green-400" : managerRow.gd < 0 ? "text-red-700 dark:text-red-400" : "text-foreground")}>
                    {managerRow.gd > 0 ? '+' : ''}{managerRow.gd}
                  </span>
                </div>
                <div className="rounded-md border bg-muted/30 px-3 py-1.5 text-sm">
                  <span className="text-muted-foreground">P</span>{" "}
                  <span className="font-semibold text-foreground">{managerRow.played}</span>
                </div>
              </div>
            );
          })()}

          {/* Advanced: Custom Date Range */}
          <Collapsible>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-1 text-muted-foreground text-xs px-0">
                <CalendarIcon className="h-3 w-3" />
                Custom date range
                <ChevronDown className="h-3 w-3" />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-2">
              <div className="flex flex-wrap gap-2">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full sm:w-[240px] justify-start text-left font-normal",
                        !fromDate && "text-muted-foreground",
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {fromDate ? format(fromDate, "PPP") : <span>From date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={fromDate}
                      onSelect={setFromDate}
                      defaultMonth={fromDate}
                      initialFocus
                      className="pointer-events-auto"
                      disabled={(date) => {
                        if (!minDate || !maxDate) return true;
                        return date < minDate || date > maxDate;
                      }}
                    />
                  </PopoverContent>
                </Popover>

                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full sm:w-[240px] justify-start text-left font-normal",
                        !toDate && "text-muted-foreground",
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {toDate ? format(toDate, "PPP") : <span>To date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={toDate}
                      onSelect={setToDate}
                      defaultMonth={toDate}
                      initialFocus
                      className="pointer-events-auto"
                      disabled={(date) => {
                        if (!minDate || !maxDate) return true;
                        return date < minDate || date > maxDate;
                      }}
                    />
                  </PopoverContent>
                </Popover>

                {(fromDate || toDate) && (
                  <Button
                    variant="ghost"
                    onClick={() => {
                      if (selectedAssignment) {
                        setFromDate(new Date(selectedAssignment.start_date));
                        setToDate(selectedAssignment.end_date ? new Date(selectedAssignment.end_date) : new Date());
                        setSelectedSeason("");
                      }
                    }}
                  >
                    Full tenure
                  </Button>
                )}
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* Table Controls */}
          {table.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 border-t pt-4">
              <ToggleGroup
                type="single"
                value={venueFilter}
                onValueChange={(v) => v && setVenueFilter(v as VenueFilter)}
                size="sm"
              >
                <ToggleGroupItem value="all" aria-label="All matches">All</ToggleGroupItem>
                <ToggleGroupItem value="home" aria-label="Home matches">
                  <Home className="mr-1 h-3 w-3" /> Home
                </ToggleGroupItem>
                <ToggleGroupItem value="away" aria-label="Away matches">
                  <Plane className="mr-1 h-3 w-3" /> Away
                </ToggleGroupItem>
              </ToggleGroup>
              <Button variant="outline" size="sm" onClick={() => setShowManagers(!showManagers)}>
                {showManagers ? "Hide Other Managers" : "Show Other Managers"}
              </Button>
              {!IMAGE_SHARING_ENABLED ? null : user ? (
                <Button variant="outline" size="sm" onClick={() => setShowShareCustomizer(true)}>
                  <Share2 className="mr-2 h-4 w-4" />
                  Share
                </Button>
              ) : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={0}>
                      <Button variant="outline" size="sm" disabled>
                        <Share2 className="mr-2 h-4 w-4" />
                        Share
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>Sign in to share</TooltipContent>
                </Tooltip>
              )}
            </div>
          )}

          {/* League Table */}
          {tableLoading && table.length === 0 ? (
            <div className="space-y-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : table.length > 0 ? (
            <div className="w-full">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b-2 border-border">
                    <th className="text-left py-3 px-2 font-semibold text-sm text-foreground w-8 sm:w-12">#</th>
                    <th className="text-left py-3 px-2 font-semibold text-sm text-foreground min-w-0">Team</th>
                    {showManagers && (
                      <th className="hidden lg:table-cell text-left py-3 px-2 font-semibold text-sm text-foreground min-w-32">
                        Manager
                      </th>
                    )}
                    <th className="text-center py-3 px-2 font-semibold text-sm text-foreground w-10 sm:w-16">P</th>
                    <th className="hidden md:table-cell text-center py-3 px-2 font-semibold text-sm text-foreground w-16">W</th>
                    <th className="hidden md:table-cell text-center py-3 px-2 font-semibold text-sm text-foreground w-16">D</th>
                    <th className="hidden md:table-cell text-center py-3 px-2 font-semibold text-sm text-foreground w-16">L</th>
                    <th className="hidden sm:table-cell text-center py-3 px-2 font-semibold text-sm text-foreground w-16">GF</th>
                    <th className="hidden sm:table-cell text-center py-3 px-2 font-semibold text-sm text-foreground w-16">GA</th>
                    <th className="text-center py-3 px-2 font-semibold text-sm text-foreground w-10 sm:w-16">GD</th>
                    <th className="text-center py-3 px-2 font-semibold text-sm text-foreground w-10 sm:w-16 bg-primary/5">Pts</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedTable.map((row, index) => {
                    const isManagerTeam = row.club_name === managerInfo.club_name;
                    const colors = getPositionColor(row.position, filteredTable.length, isManagerTeam, leagueConfig);

                    return (
                      <tr
                        key={index}
                        className={`border-b border-border hover:bg-muted/50 transition-colors ${
                          isManagerTeam ? `font-semibold ${colors.bgColor}` : colors.bgColor
                        }`}
                      >
                        <td className={`py-3 px-2 text-sm ${colors.posColor || "text-foreground"} font-semibold`}>
                          {row.position}
                        </td>
                        <td className={`py-3 px-2 text-sm ${colors.textColor || "text-foreground"}`}>
                          {(() => {
                            const clubSlug = getClubSlug(row.club_id);
                            const inner = (
                              <>
                                <ClubCrest clubName={row.club_name} size="sm" />
                                <span className="truncate max-w-[110px] sm:max-w-[160px] md:max-w-none">
                                  {row.club_name}
                                </span>
                              </>
                            );
                            return clubSlug ? (
                              <Link
                                to={`/club/${clubSlug}`}
                                className="flex w-full items-center gap-2 text-left transition-colors hover:text-primary hover:underline"
                              >
                                {inner}
                              </Link>
                            ) : (
                              <span className="flex w-full items-center gap-2">{inner}</span>
                            );
                          })()}
                        </td>

                        {showManagers && (
                          <td className="hidden lg:table-cell py-3 px-2 text-sm text-muted-foreground">
                            {row.managers && row.managers.length > 0 ? row.managers.join(", ") : "—"}
                          </td>
                        )}
                        <td className="py-3 px-2 text-sm text-center text-foreground">{row.played}</td>
                        <td className="hidden md:table-cell py-3 px-2 text-sm text-center text-foreground">{row.won}</td>
                        <td className="hidden md:table-cell py-3 px-2 text-sm text-center text-foreground">{row.drawn}</td>
                        <td className="hidden md:table-cell py-3 px-2 text-sm text-center text-foreground">{row.lost}</td>
                        <td className="hidden sm:table-cell py-3 px-2 text-sm text-center text-foreground">{row.gf}</td>
                        <td className="hidden sm:table-cell py-3 px-2 text-sm text-center text-foreground">{row.ga}</td>
                        <td
                          className={`py-3 px-2 text-sm text-center ${
                            row.gd > 0 ? "text-primary" : row.gd < 0 ? "text-destructive" : "text-foreground"
                          }`}
                        >
                          {row.gd > 0 ? "+" : ""}
                          {row.gd}
                        </td>
                        <td className="py-3 px-2 text-sm text-center font-semibold text-foreground bg-primary/5">
                          {row.pts}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {filteredTable.length > defaultTeamCount && (
                <div className="mt-4 text-center">
                  <Button variant="outline" onClick={() => setShowAllTeams(!showAllTeams)}>
                    {showAllTeams ? `Show Top ${defaultTeamCount} Teams` : `Show All Teams (${filteredTable.length})`}
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">No matches found for this tenure</p>
          )}
        </CardContent>
      </Card>

      {/* Share Customizer Dialog */}
      {table.length > 0 && (() => {
        const managerRow = filteredTable.find((row) => row.club_name === managerInfo.club_name);
        const managerPlayed = managerRow?.played || 0;
        const threshold = Math.floor(managerPlayed * 0.9);
        const shareFilteredTable = filteredTable.filter((row) => row.played >= threshold);
        const renumberedTable = shareFilteredTable.map((row, index) => ({
          position: index + 1,
          team: row.club_name,
          played: row.played,
          won: row.won,
          drawn: row.drawn,
          lost: row.lost,
          gf: row.gf,
          ga: row.ga,
          gd: row.gd,
          points: row.pts,
          isManagerTeam: row.club_name === managerInfo.club_name,
        }));

        return (
          <ShareableTableCustomizer
            open={showShareCustomizer && IMAGE_SHARING_ENABLED}
            onOpenChange={setShowShareCustomizer}
            managerName={managerInfo.name}
            clubName={managerInfo.club_name}
            tenurePeriod={`${new Date(managerInfo.start_date).toLocaleDateString()} - ${managerInfo.end_date ? new Date(managerInfo.end_date).toLocaleDateString() : "Present"}`}
            season={selectedSeason ? `${selectedSeason}${venueFilter !== "all" ? ` (${venueFilter} only)` : ""}` : venueFilter !== "all" ? `${venueFilter} only` : undefined}
            dateRange={
              fromDate && toDate
                ? `${format(fromDate, "PP")} - ${format(toDate, "PP")}`
                : undefined
            }
            tableData={renumberedTable}
            managerId={managerId}
            assignmentId={selectedAssignmentId}
          />
        );
      })()}
    </>
  );
};
